/*
 * Copyright (c) 2022-2022. Revo Digital
 * ---
 * Author: gabriele
 * File: general.controls.ts
 * Project: mle-client-n
 * Committed last: 2022/1/28 @ 1618
 * ---
 * Description:
 */

import {Editor} from '../../../../store/observables/Editor';
import {
  ButtonGroupShapeControl,
  ControlGroup,
  controlGroupBuilder,
  ControlGroupLayouts,
  ShapeControl
} from '../../helpers/ControlGroup';
import {ALL_SHAPE_TYPES, ShapeTypes} from '../../../enums';
import {ControlTypes} from '../../../selection-properties';
import {ObservableCircle} from '../../../../store/observables/ObservableCircle';
import {ObservableRect} from '../../../../store/observables/ObservableRect';
import {Nullable} from '../../../interfaces';
import {defaultSetterOptions} from '../../index';
import {NumericShapeControl} from '../../helpers/NumericInputShapeControl';
import {defaultMeasureUnitTransformer} from '../../helpers/Transformers';
import {ObservableText} from '../../../../store/observables/ObservableText';
import {SelectSCItem, SelectShapeControl, valuesToSelectItems} from '../../helpers/SelectShapeControl';
import {ObservableRichText} from '../../../../store/observables/ObservableRichText';
import {BooleanShapeControl} from '../../helpers/BooleanShapeControl';
import {AlignmentShapeControl} from '../../helpers/AlignmentShapeControl';
import {ObservableBarcode} from '../../../../store/observables/ObservableBarcode';

export enum LayerActions {
  FORWARD = 'FORWARD',
  BACKWARD = 'BACKWARD',
  FRONT = 'FRON',
  BACK = 'BACK'
}

export enum FontStyles {
  BOLD = 'bold',
  ITALIC = 'italic',
  NORMAL = 'normal'
}

export enum FontDecorations {
  UNDERLINE = 'underline',
  NORMAL = 'normal'
}

export const composeFontAdditions = (incomingStyle: FontStyles | FontDecorations, currentStyles: string): string => {
  const stylesArr = currentStyles.split(' ');

  if (incomingStyle === FontStyles.NORMAL) return FontStyles.NORMAL;
  if (currentStyles.includes(incomingStyle))
    return stylesArr.filter(item => item !== incomingStyle).join(' ');
  return [ ...stylesArr, incomingStyle ].filter(item => item !== FontStyles.NORMAL).join(
    ' ');
};

export const Fonts: SelectSCItem<string>[] = valuesToSelectItems([
  'Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Georgia'
]);

export const positionControlGroup = (editor: Editor) => new ControlGroup({
  title: 'Posizione',
  layout: ControlGroupLayouts.FLEX_ROW,
  controlledShapeTypes: ALL_SHAPE_TYPES,
  shapeControls: [
    new NumericShapeControl({
      label: 'X',
      controlType: ControlTypes.NUMBER,
      displayMeasureUnit: true,
      observableValue: editor.getSelectedShape()?.x as number,
      setter: (value: number) => editor.getSelectedShape()?.setX(value,
        defaultSetterOptions(editor)),
      transformer: defaultMeasureUnitTransformer(editor)
    }, { min: 0 }),
    new NumericShapeControl({
      label: 'Y',
      controlType: ControlTypes.NUMBER,
      displayMeasureUnit: true,
      observableValue: editor.getSelectedShape()?.y as number,
      setter: (value: number) => editor.getSelectedShape()?.setY(value,
        defaultSetterOptions(editor)),
      transformer: defaultMeasureUnitTransformer(editor)
    }, { min: 0 }),
  ]
});

export const rotationControlGroup = (editor: Editor) => new ControlGroup({
  title: 'Rotazione',
  layout: ControlGroupLayouts.FLEX_ROW,
  controlledShapeTypes: ALL_SHAPE_TYPES,
  shapeControls: [
    new NumericShapeControl({
      label: 'Rotazione',
      controlType: ControlTypes.NUMBER,
      measureUnitOverride: 'gradi',
      observableValue: editor.getSelectedShape()?.rotation as number,
      setter: (value: number) => editor.getSelectedShape()?.setRotation(value,
        defaultSetterOptions(editor)),
    }, { min: 0, max: 360 })
  ]
});

export const dimensionsControlGroup = (editor: Editor) => new ControlGroup({
  title: 'Dimensioni',
  layout: ControlGroupLayouts.FLEX_ROW,
  controlledShapeTypes: ALL_SHAPE_TYPES,
  shapeControls: [
    new NumericShapeControl({
      label: 'Larghezza',
      controlType: ControlTypes.NUMBER,
      displayMeasureUnit: true,
      observableValue: editor.getSelectedShape()?.width as number,
      setter: (value: number) => editor.getSelectedShape()?.setWidth(value,
        defaultSetterOptions(editor)),
      transformer: defaultMeasureUnitTransformer(editor)
    }, { min: 1 }),
    new NumericShapeControl({
      label: 'Altezza',
      controlType: ControlTypes.NUMBER,
      displayMeasureUnit: true,
      observableValue: editor.getSelectedShape()?.height as number,
      setter: (value: number) => editor.getSelectedShape()?.setHeight(value,
        defaultSetterOptions(editor)),
      transformer: defaultMeasureUnitTransformer(editor),
    }, { min: 1 }),
  ]
});

export const circleControlGroup = (editor: Editor) => new ControlGroup({
  title: 'Cerchio',
  layout: ControlGroupLayouts.WHOLE_ROW,
  controlledShapeTypes: [ ShapeTypes.CIRCLE ],
  shapeControls: [ new NumericShapeControl({
    label: 'Raggio',
    controlType: ControlTypes.NUMBER,
    displayMeasureUnit: true,
    observableValue: (editor.getSelectedShape() as ObservableCircle).radius as number,
    setter: (value: number) => (editor.getSelectedShape() as ObservableCircle).setRadius(
      value,
      defaultSetterOptions(editor)),
    transformer: defaultMeasureUnitTransformer(editor)
  }, { min: 1 }) ]
});

export const fillControlGroup = (editor: Editor) => new ControlGroup({
  title: 'Colori',
  layout: ControlGroupLayouts.WHOLE_ROW,
  controlledShapeTypes: [ ShapeTypes.RECT, ShapeTypes.CIRCLE, ShapeTypes.TRIANGLE, ShapeTypes.ELLIPSE, ShapeTypes.WEDGE, ShapeTypes.TEXT, ShapeTypes.RICH_TEXT, ],
  shapeControls: [ new ShapeControl<string>({
    label: 'Riempimento',
    controlType: ControlTypes.COLOR,
    measureUnitOverride: 'Cod. colore',
    observableValue: editor.getSelectedShape()?.fill as string,
    setter: (value: string) => editor.getSelectedShape()?.setFill(value,
      defaultSetterOptions(editor))
  }) ]
});

export const strokeControlGroup = (editor: Editor) => new ControlGroup({
  title: 'Bordo',
  layout: ControlGroupLayouts.FLEX_ROW,
  controlledShapeTypes: [ ShapeTypes.RECT, ShapeTypes.CIRCLE, ShapeTypes.TRIANGLE, ShapeTypes.ELLIPSE, ShapeTypes.WEDGE, ShapeTypes.RICH_TEXT, ShapeTypes.IMAGE ],
  shapeControls: [
    new NumericShapeControl({
      label: 'Spessore',
      controlType: ControlTypes.NUMBER,
      displayMeasureUnit: true,
      observableValue: editor.getSelectedShape()?.stroke.width as number,
      setter: (value: number) => editor.getSelectedShape()?.stroke.setWidth(value,
        defaultSetterOptions(editor)),
      transformer: defaultMeasureUnitTransformer(editor)
    }, { min: 0 }),
    new ShapeControl({
      label: 'Riempimento',
      controlType: ControlTypes.COLOR,
      measureUnitOverride: 'Cod. colore',
      observableValue: editor.getSelectedShape()?.stroke.fill as string,
      setter: (value: string) => editor.getSelectedShape()?.stroke.setFill(value,
        defaultSetterOptions(editor))
    }),
    new NumericShapeControl({
      label: 'Raggio',
      controlType: ControlTypes.NUMBER,
      displayMeasureUnit: true,
      observableValue: (editor.getSelectedShape() as ObservableRect).cornerRadius as number,
      setter: (value: number) => (editor.getSelectedShape() as ObservableRect).setCornerRadius(
        value,
        defaultSetterOptions(editor)),
      transformer: defaultMeasureUnitTransformer(editor),
      controlledShapesOverride: [ShapeTypes.RECT]
    }, { min: 0 })
  ]
});

export const marginControlGroup = (editor: Editor) => controlGroupBuilder({
  title: 'Spaziatura',
  layout: ControlGroupLayouts.FLEX_ROW,
  controlledShapeTypes: [ ShapeTypes.RICH_TEXT ],
  shapeControls: [ new NumericShapeControl({
    label: 'Margine',
    controlType: ControlTypes.NUMBER,
    displayMeasureUnit: true,
    observableValue: editor.getSelectedShape<ObservableRichText>().margin,
    setter: value => editor.getSelectedShape<ObservableRichText>().setMargin(
      value,
      { applyToPamelaShape: true })
  }, { min: 0 }) ]
});

export const layerControlGroup = (editor: Editor) => new ControlGroup({
  title: 'Livelli',
  layout: ControlGroupLayouts.WHOLE_ROW,
  controlledShapeTypes: ALL_SHAPE_TYPES,
  shapeControls: [ new ButtonGroupShapeControl<Nullable<LayerActions>>({
      label: 'Movimento',
      controlType: ControlTypes.BUTTON_GROUP,
      displayMeasureUnit: false,
      observableValue: null,
      setter: (value: Nullable<LayerActions>) => {
        switch (value) {
          case LayerActions.BACK:
            editor.getSelectedShape()?.pamelaShape__nonObservable.moveDown();
            break;
          case LayerActions.BACKWARD:
            editor.getSelectedShape()?.pamelaShape__nonObservable.moveToBottom();
            break;
          case LayerActions.FRONT:
            editor.getSelectedShape()?.pamelaShape__nonObservable.moveUp();
            break;
          case LayerActions.FORWARD:
            editor.getSelectedShape()?.pamelaShape__nonObservable.moveToTop();
            break;
          default:
            throw new Error(`Azione ${ value } non gestita dal setter`);
        }
        // TODO: Gestire riposizionamento nel nodes tree delle forme
        // work-area.reloadShapesFromNodesTree();
      }
    },
    [ {
      name: LayerActions.FRONT,
      value: LayerActions.FRONT,
      icon: 'chevron-up'
    }, {
      name: LayerActions.FORWARD,
      value: LayerActions.FORWARD,
      icon: 'double-chevron-up'
    }, {
      name: LayerActions.BACK,
      value: LayerActions.BACK,
      icon: 'chevron-down'
    }, {
      name: LayerActions.BACKWARD,
      value: LayerActions.BACKWARD,
      icon: 'double-chevron-down'
    } ]) ]
});

export const generalTextControls = (editor: Editor) => controlGroupBuilder({
  title: 'Testo',
  layout: ControlGroupLayouts.FLEX_ROW,
  controlledShapeTypes: [ ShapeTypes.TEXT, ShapeTypes.RICH_TEXT ],
  shapeControls: [
    new NumericShapeControl({
      label: 'Dimensione',
      controlType: ControlTypes.NUMBER,
      displayMeasureUnit: false,
      measureUnitOverride: 'pt',
      isReadonly: editor.getSelectedShape<ObservableText>().lockSize,
      observableValue: editor.getSelectedShape<ObservableText>().fontSize,
      setter: (value) =>
        editor.getSelectedShape<ObservableText>().setFontSize(
          value,
          { applyToPamelaShape: true }),
      transformer: defaultMeasureUnitTransformer(editor)
    }, { min: 6 }),
    new NumericShapeControl({
      label: 'Dimensioni font contenuto',
      controlType: ControlTypes.NUMBER,
      measureUnitOverride: 'pt',
      observableValue: editor.getSelectedShape<ObservableBarcode>()?.contentFontSize,
      controlledShapesOverride: [ ShapeTypes.BARCODE ],
      setter: (value) => editor.getSelectedShape<ObservableBarcode>()?.setContentFontSize(
        value,
        defaultSetterOptions(editor))
    }, { min: 1 }),
    new BooleanShapeControl({
      label: 'Adatta dimensioni',
      controlType: ControlTypes.CHECKBOX,
      displayMeasureUnit: false,
      measureUnitOverride: 'Adatta le dimensioni della casella e riduci la dimensione del font',
      observableValue: editor.getSelectedShape<ObservableText>().lockSize,
      setter: (value) =>
        editor.getSelectedShape<ObservableText>().setLockSize(value,
          { applyToPamelaShape: true })
    }),
  ]
});

export const fontStyleControls = (editor: Editor) => controlGroupBuilder({
  title: 'Stile Testo',
  layout: ControlGroupLayouts.FLEX_ROW,
  controlledShapeTypes: [ ShapeTypes.TEXT ],
  shapeControls: [
    new ButtonGroupShapeControl<string>({
        label: 'Stili',
        controlType: ControlTypes.BUTTON_GROUP,
        observableValue: editor.getSelectedShape<ObservableText>().fontStyle,
        setter: (value) => editor.getSelectedShape<ObservableText>().setFontStyle(
          composeFontAdditions(value as FontStyles,
            editor.getSelectedShape<ObservableText>().fontStyle),
          { applyToPamelaShape: true })
      },
      [
        {
          name: 'Grassetto',
          icon: 'bold',
          value: FontStyles.BOLD
        },
        {
          name: 'Corsivo',
          icon: 'italic',
          value: FontStyles.ITALIC
        } ]),
    new ButtonGroupShapeControl<string>({
        label: 'Altre decorazioni',
        controlType: ControlTypes.BUTTON_GROUP,
        observableValue: editor.getSelectedShape<ObservableText>().textDecoration,
        setter: (value) => editor.getSelectedShape<ObservableText>().setTextDecoration(
          composeFontAdditions(value as FontDecorations,
            editor.getSelectedShape<ObservableText>().textDecoration),
          { applyToPamelaShape: true })
      },
      [ {
        name: 'Linea sotto',
        value: 'underline',
        icon: 'underline'
      } ])
  ]
});

export const fontControls = (editor: Editor) => controlGroupBuilder({
  title: 'Carattere',
  controlledShapeTypes: [ ShapeTypes.TEXT ],
  layout: ControlGroupLayouts.FLEX_ROW,
  shapeControls: [
    new SelectShapeControl({
      label: 'Font',
      controlType: ControlTypes.SELECT,
      displayMeasureUnit: false,
      observableValue: (editor.getSelectedShape() as ObservableText).fontFamily,
      setter: (value) => editor.getSelectedShape<ObservableText>().setFontFamily(
        value,
        { applyToPamelaShape: true })
    }, { items: Fonts }),
    new AlignmentShapeControl({
      label: 'Allineamento',
      measureUnitOverride: 'Allinemento orizzontale',
      observableValue: (editor.getSelectedShape() as ObservableText).horizontalAlignment,
      setter: (value) => (editor.getSelectedShape() as ObservableText).setHorizontalAlignment(
        value, { applyToPamelaShape: true }),
      controlType: ControlTypes.ALIGN_SELECT,
    }, { enableJustify: true }),
  ]
});

export const lineControls = (editor: Editor) => controlGroupBuilder({
  title: 'Righe',
  controlledShapeTypes: [ ShapeTypes.TEXT ],
  layout: ControlGroupLayouts.FLEX_ROW,
  shapeControls: [
    new NumericShapeControl({
      label: 'Interlinea',
      measureUnitOverride: 'Altezza righe',
      observableValue: (editor.getSelectedShape() as ObservableText).lineHeight,
      setter: value => (editor.getSelectedShape() as ObservableText).setLineHeight(
        value,
        { applyToPamelaShape: true }),
      controlType: ControlTypes.NUMBER,
    }, { min: 0.0, step: 0.1 })
  ]
});

export const richTextGenerals = (editor: Editor) => controlGroupBuilder({
  title: 'Testo multistile',
  layout: ControlGroupLayouts.FLEX_ROW,
  controlledShapeTypes: [ ShapeTypes.RICH_TEXT, ShapeTypes.TEXT ],
  shapeControls: [ new ShapeControl({
    label: 'Sfondo testo',
    controlType: ControlTypes.COLOR,
    observableValue: editor.getSelectedShape<ObservableRichText>().backgroundColor,
    setter: (value) => editor.getSelectedShape<ObservableRichText>().setBackgroundColor(
      value,
      { applyToPamelaShape: true })
  }) ]
});

export const barcodeColors = (editor: Editor) => controlGroupBuilder({
  title: 'Colori',
  layout: ControlGroupLayouts.FLEX_ROW,
  controlledShapeTypes: [ ShapeTypes.BARCODE ],
  shapeControls: [
    new ShapeControl<string>({
      label: 'Riempimento',
      controlType: ControlTypes.COLOR,
      measureUnitOverride: 'Cod. colore',
      observableValue: editor.getSelectedShape()?.fill as string,
      setter: (value: string) => editor.getSelectedShape()?.setFill(value,
        defaultSetterOptions(editor))
    }),
    new ShapeControl({
      label: 'Sfondo',
      measureUnitOverride: 'Colore di sfondo',
      controlType: ControlTypes.COLOR,
      observableValue: editor.getSelectedShape<ObservableBarcode>().backgroundColor,
      setter: (value) => editor.getSelectedShape<ObservableBarcode>().setBackgroundColor(
        value,
        { applyToPamelaShape: true }),
    }) ]
});

